import {Lazy} from '../utils/lazy';

export type Entry<T> = {
  head: Lazy<T>;
  tail: LazyList<T>;
} | null;

export type LazyList<T> = Lazy<Entry<T>>

export function map<T, R>(block: (T) => R, list: LazyList<T>): LazyList<R> {
  return () => {
    const source = list();
    if (source == null) {
      return null;
    } else {
      return {
        head: () => block(source.head()),
        tail: map(block, source.tail)
      };
    }
  };
}

export function mapAtIndex<T, R>(block: (T) => T, targetIndex: number, list: LazyList<T>, currentIndex: number = 0): LazyList<T> {
  return () => {
    const source = list();
    if (source == null) {
      return null;
    } else {
      if (targetIndex === currentIndex) {
        return {
          head: () => block(source.head()),
          tail: source.tail
        };
      } else {
        return {
          head: source.head,
          tail: mapAtIndex(block, targetIndex, source.tail, currentIndex + 1)
        };
      }
    }
  };
}

export function newTree<T>(size: number, block: Lazy<T>): LazyList<T> {
  return () => {
    return size === 0 ? null : {
      head: () => block(),
      tail: newTree(size - 1, block)
    };
  };
}

export function range(start: number = 1): LazyList<number> {
  return () => ({
    head: () => start,
    tail: range(start + 1)
  });
}

export function take<T>(count: number, source: LazyList<T>): LazyList<T> {
  return () => {
    const list = source();
    return (count === 0 || list == null) ?
      null
      :
      {
        head: () => list.head(),
        tail: take(count - 1, list.tail)
      }
  };
}

export function toTree<T>(source: T[]): LazyList<T> {
  return () => {
    return source.length === 0 ? null : {
      head: () => source[0],
      tail: toTree(source.slice(1))
    };
  };
}

export function toArray<T>(list: LazyList<T>): T[] {
  let root = list();
  let result = [] as T[];

  while (root != null) {
    result = result.concat(root.head());
    root = root.tail();
  }

  return result;
}

export function last<T>(list: LazyList<T>): Lazy<T | null> {
  let root = list();
  if (root == null) {
    return () => null;
  } else {
    let tail = root.tail();
    while (tail != null) {
      root = tail;
      tail = root.tail();
    }
    return root.head;
  }
}

export function getIndex<T>(index: number, list: LazyList<T>): Lazy<T | null> {
  return () => {
    let root = list();
    let currentIndex = 0;

    while (root !== null && currentIndex < index) {
      root = root.tail();
      currentIndex = currentIndex + 1;
    }

    if (root == null) {
      return null;
    } else {
      return root.head();
    }
  };
}
